import apiEndPoints from '../../apiconstants';
import { api, handleErrorResponse, responseArgs } from '../api';

export interface MapperDefinition {
  mapperId?: string;
  status?: string;
  version?: string | number;
  domain?: string;
  direction?: string;
  integrationId?: string;
  /** Total source/target fields in the mapper payload (when provided by the API). */
  fields_count?: number;
  /** Total mapping rows (when provided by the API). */
  total_mappings?: number;
  /** Count of mappings that matched the default pairing (when provided by the API). */
  default_mapped_count?: number;
  /** Count of mappings not default-mapped (when provided by the API). */
  default_unmapped_count?: number;
  /** Share of default-mapped rows as a percentage (when provided by the API). */
  default_mapped_percentage?: number;
  /** Mapped share as a percentage (when provided by the API); shown in the Field Mappings header. */
  mapped_percentage?: number;
  mappings?: MapperMapping[];
}

export interface MapperMapping {
  id?: string;
  source?: string;
  destination?: string;
  /** Model confidence for this mapping (0–1 or 0–100 depending on API). */
  confidence_score?: number | string;
  [key: string]: unknown;
}

export interface MapperField {
  path: string;
  type?: string;
  sample_value?: unknown;
  parent?: string;
  depth?: number;
}

export interface GenerateMapperResponseData {
  source_fields?: MapperField[];
  target_fields?: MapperField[];
  mapper_definition?: MapperDefinition;
  mapper_status?: string;
  mapper_version?: string | number;
}

export interface GenerateMapperResponse {
  status_code?: number;
  message?: string;
  data?: GenerateMapperResponseData;
}

export interface MapperItem {
  aiworker_id: string;
  name?: string;
  description?: string;
  status?: string;
  mapper_status?: string;
  mapper_version?: string | number;
  mapper_definition?: MapperDefinition;
  source_fields?: MapperField[];
  target_fields?: MapperField[];
  code?: string | null;
}

export interface GenerateMapperRequest {
  aiworker_id: string;
  source_sample: Record<string, unknown>;
  target_sample: Record<string, unknown>;
  integration_id?: string;
  direction?: string;
  domain?: string;
  user_prompt_append?: string;
  aligned_examples?: boolean;
}

export interface UpdateMapperRequest {
  aiworker_id: string;
  mapper_definition: MapperDefinition;
  mapper_source_fields?: MapperField[] | null;
  mapper_target_fields?: MapperField[] | null;
}

export interface UpdateMapperResponse {
  status_code?: number;
  message?: string;
  data?: MapperItem;
}

export interface ApproveMapperRequest {
  aiworker_id: string;
  action: 'approve' | 'reject';
  comments: string;
}

export interface ApproveMapperResponse {
  status_code?: number;
  message?: string;
  data?: MapperItem;
}

export interface ExecuteMapperRequest {
  aiworker_id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  source_data: Record<string, unknown>;
}

export interface ExecuteMapperResponse {
  status_code?: number;
  message?: string;
  data?: unknown;
  [key: string]: unknown;
}

export interface DeleteMapperResponse {
  status_code?: number;
  message?: string;
  data?: unknown;
}

interface MapperAttrItem {
  attr_name?: string;
  attr_value?: string | null;
  attr_json?: unknown;
}

function normalizeMapperItem(rawMapper: Record<string, unknown>): MapperItem {
  const aiworkerAttrs = Array.isArray(rawMapper.aiworkers_attrs)
    ? (rawMapper.aiworkers_attrs as MapperAttrItem[])
    : [];
  const mapperAttrs =
    rawMapper.mapper_attrs && typeof rawMapper.mapper_attrs === 'object'
      ? (rawMapper.mapper_attrs as Record<string, unknown>)
      : {};

  const definitionFromAttrs =
    (rawMapper.mapper_definition as MapperDefinition | undefined) ??
    (mapperAttrs.mapper_definition as MapperDefinition | undefined) ??
    ((aiworkerAttrs.find((attr) => attr.attr_name === 'mapper_definition')
      ?.attr_json as MapperDefinition | undefined) ??
      undefined);

  const sourceFields =
    (rawMapper.source_fields as MapperField[] | undefined) ??
    (mapperAttrs.mapper_source_fields as MapperField[] | undefined) ??
    ((aiworkerAttrs.find((attr) => attr.attr_name === 'mapper_source_fields')
      ?.attr_json as MapperField[] | undefined) ??
      undefined);

  const targetFields =
    (rawMapper.target_fields as MapperField[] | undefined) ??
    (mapperAttrs.mapper_target_fields as MapperField[] | undefined) ??
    ((aiworkerAttrs.find((attr) => attr.attr_name === 'mapper_target_fields')
      ?.attr_json as MapperField[] | undefined) ??
      undefined);

  const mapperStatus =
    (rawMapper.mapper_status as string | undefined) ??
    (mapperAttrs.mapper_status as string | undefined) ??
    (aiworkerAttrs.find((attr) => attr.attr_name === 'mapper_status')?.attr_value ??
      undefined) ??
    (definitionFromAttrs?.status ?? undefined);

  const mapperVersion =
    (rawMapper.mapper_version as string | number | undefined) ??
    (mapperAttrs.mapper_version as string | number | undefined) ??
    (aiworkerAttrs.find((attr) => attr.attr_name === 'mapper_version')?.attr_value ??
      undefined) ??
    (definitionFromAttrs?.version ?? undefined);

  return {
    aiworker_id: String(rawMapper.aiworker_id ?? ''),
    name: (rawMapper.name as string | undefined) ?? undefined,
    description: (rawMapper.description as string | undefined) ?? undefined,
    status: (rawMapper.status as string | undefined) ?? undefined,
    mapper_status: mapperStatus,
    mapper_version: mapperVersion,
    mapper_definition: definitionFromAttrs,
    source_fields: sourceFields,
    target_fields: targetFields,
    code: (rawMapper.code as string | null | undefined) ?? undefined,
  };
}

function normalizeMapperByIdResponse(response: responseArgs): MapperItem {
  const data = response?.data;
  if (Array.isArray(data)) {
    return normalizeMapperItem((data[0] ?? {}) as Record<string, unknown>);
  }
  return normalizeMapperItem((data ?? {}) as Record<string, unknown>);
}

export const mapperApiSlice = api.injectEndpoints({
  endpoints: (builder) => ({
    getMappersList: builder.query<MapperItem[], void>({
      query: () => ({
        url: apiEndPoints.getMappers,
        method: 'GET',
      }),
      transformResponse: (response: responseArgs) => (response?.data ?? []) as MapperItem[],
      transformErrorResponse: handleErrorResponse,
    }),
    getMapperById: builder.query<MapperItem, string>({
      query: (mapperId) => ({
        url: `${apiEndPoints.mapper}/${mapperId}`,
        method: 'GET',
      }),
      transformResponse: (response: responseArgs) => normalizeMapperByIdResponse(response),
      transformErrorResponse: handleErrorResponse,
    }),
    generateMapper: builder.mutation<GenerateMapperResponse, GenerateMapperRequest>({
      query: (payload) => ({
        url: apiEndPoints.generateMapper,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: responseArgs) => response as GenerateMapperResponse,
      transformErrorResponse: handleErrorResponse,
    }),
    updateMapper: builder.mutation<UpdateMapperResponse, UpdateMapperRequest>({
      query: ({ aiworker_id, ...payload }) => ({
        url: `${apiEndPoints.mapper}/${aiworker_id}`,
        method: 'PATCH',
        body: payload,
      }),
      transformResponse: (response: responseArgs) => ({
        status_code: response?.status_code,
        message: response?.message,
        data:
          response?.data && typeof response.data === 'object'
            ? normalizeMapperItem(response.data as Record<string, unknown>)
            : undefined,
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    approveMapper: builder.mutation<ApproveMapperResponse, ApproveMapperRequest>({
      query: ({ aiworker_id, ...payload }) => ({
        url: `${apiEndPoints.mapper}/${aiworker_id}/approve`,
        method: 'PATCH',
        body: payload,
      }),
      transformResponse: (response: responseArgs) => ({
        status_code: response?.status_code,
        message: response?.message,
        data:
          response?.data && typeof response.data === 'object'
            ? normalizeMapperItem(response.data as Record<string, unknown>)
            : undefined,
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    executeMapper: builder.mutation<ExecuteMapperResponse, ExecuteMapperRequest>({
      query: (payload) => ({
        url: apiEndPoints.executeMapper,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: responseArgs) => response as ExecuteMapperResponse,
      transformErrorResponse: handleErrorResponse,
    }),
    deleteMapper: builder.mutation<DeleteMapperResponse, string>({
      query: (mapperId) => ({
        url: `${apiEndPoints.mapper}/${mapperId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: responseArgs) => response as DeleteMapperResponse,
      transformErrorResponse: handleErrorResponse,
    }),
  }),
});

export const {
  useGetMappersListQuery,
  useGetMapperByIdQuery,
  useGenerateMapperMutation,
  useUpdateMapperMutation,
  useApproveMapperMutation,
  useExecuteMapperMutation,
  useDeleteMapperMutation,
} = mapperApiSlice;
